import {
  setTest,
  setResult,
  resetDataTest,
  setCurrentPage,
  setGroupQuestions,
} from 'app/actions/tests';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { AudioProvider } from 'app/components/tests/media/AudioManager';
import ModalReadyTest from 'app/components/tests/modal/ModalReadyTest';
import TestFooter from 'app/components/tests/layouts/TestFooter';
import TestHeader from 'app/components/tests/layouts/TestHeader';
import TestMenu from 'app/components/tests/layouts/TestMenu';
import GroupQuestion from 'app/components/tests/GroupQuestion';
import IeltForm from 'app/components/tests/Ielts/IeltForm';
import { createUserActivity } from 'app/requests/commons';
import * as categoryConst from 'app/constants/categories';
import { withGlobalData } from 'app/utils/withGlobalData';
import { useDispatch, useSelector } from 'react-redux';
import { Group, Question, Passage } from 'types/test';
import { scrollToQuestion } from 'app/utils/test';
import { setLoading } from 'app/actions/commons';
import { AppStoreState } from 'app/redux/store';
import * as testApi from 'app/requests/tests';
import { removeClass } from 'app/utils/dom';
import { wrapper } from 'app/redux/store';
import { useState, useMemo } from 'react';
import { useRouter } from 'next/router';
import 'app/components/tests/styles.css';

export default function TestForm() {
  const [openModalReady, setOpenModalReady] = useState(false);
  const [passagesOfUser, setPassagesUser] = useState<Passage[]>([]);
  const { test, result, process, groupQuestions } = useSelector((state: AppStoreState) => state.test);

  const router = useRouter();
  const dispatch = useDispatch();

  const isIeltRead = test.category_id == categoryConst.IELT_READ;
  const isIeltSpeak = test.category_id == categoryConst.IELT_SPEAK;
  const isIeltWrite = test.category_id == categoryConst.IELT_WRITE;

  const handleResetDataTest = () => {
    dispatch(resetDataTest());
  }

  const setNewGroupQuestions = (updatedQuestion: Question) => {
    dispatch(setGroupQuestions(
      groupQuestions.map((group: Group) => {
        if (updatedQuestion.page !== group.page) return group;

        return {
          ...group,
          list_question: group.list_question.map((question: Question) =>
            question.id === updatedQuestion.id ? { ...updatedQuestion } : question
          ),
        };
      })
    ));
  };

  const handleExamScore = async () => {
    dispatch(setLoading(true));

    if (isIeltWrite) {
      await testApi.storePassagesForUser({ passages: passagesOfUser });
    }

    createUserActivity({
      'activity_type': 'tests',
      'activity_id': test?.id?.toString(),
    });

    const testResult = await testApi.getByTestSlug(test?.slug, {
      getResult: true,
      code: router?.locale,
    });

    dispatch(setResult(testResult));
    dispatch(setLoading(false));
  }

  const handleChangeQuestion = (value: string | null, question: Question) => {
    if (value && question?.number) {
    }

    setNewGroupQuestions({
      ...question,
      answer: value || null,
    })
  }

  const handleChangePage = (page: number = 0, questionId: string = '') => {
    const group = groupQuestions?.find((group: Group) => group.page == page);

    if (group) {
      const questionActiveOld = group.list_question.find(q => q.activeClass) as Question;
      setNewGroupQuestions({ ...questionActiveOld, activeClass: false })

      dispatch(setCurrentPage(page));
      scrollToQuestion(questionId, test.category_id);
    }
  }

  const activeQuestion = (question: Question) => {
    handleChangePage(question.page || 0, `#q${question.id}`);

    if (window.innerWidth < 768) {
      removeClass(".menu-question", "show-menu");
    }

    setNewGroupQuestions({ ...question, activeClass: true });

    const timeoutId = setTimeout(() => {
      setNewGroupQuestions({ ...question, activeClass: false });
    }, 1500);

    return () => clearTimeout(timeoutId);
  };

  const confirmIsReady = () => {
    if (['init', 'complete'].includes(process)) {
      setOpenModalReady(true);
    }
  }

  const numberQuestionDone = useMemo(() => {
    if (categoryConst.IELT_WRITE == test.category_id) {
      return passagesOfUser.filter((passage: Passage) => passage.content).length;
    }

    let count = 0;
    groupQuestions.forEach((group: Group) => {
      count += group.list_question.filter((question: Question) => !!question.answer)?.length;
    });

    return count;
  }, [passagesOfUser, groupQuestions]);

  return (
    <AudioProvider >
      <section id="test" className="box-card-area">
        <ModalReadyTest
          isOpen={openModalReady}
          onClose={() => setOpenModalReady(false)}
          onConfirm={() => handleResetDataTest()}
        />
        <div className="container-md">
          <div className="d-flex">
            <div className="box-news-area">
              <div className={!isIeltRead ? 'content_inner' : ''}>
                <TestHeader initDataTest={handleResetDataTest} />
                <div id="body_test">
                  <form className="form-test">
                    {!isIeltSpeak && !isIeltRead && groupQuestions?.map((group: Group, index: number) => (
                      <GroupQuestion
                        key={group.id}
                        group={group}
                        groupResult={process == 'complete' ? result?.group_questions[index] : null}
                        confirmIsReady={confirmIsReady}
                        handleChange={handleChangeQuestion}
                      />
                    ))}
                    <IeltForm
                      passagesOfUser={passagesOfUser}
                      setPassagesUser={setPassagesUser}
                      confirmIsReady={confirmIsReady}
                      handleChangeQuestion={handleChangeQuestion}
                    />
                  </form>
                </div>
                <TestFooter
                  activeQuestion={activeQuestion}
                  numberQuestionDone={numberQuestionDone}
                  handleExamScore={handleExamScore}
                  passagesOfUser={passagesOfUser}
                  initDataTest={handleResetDataTest}
                />
              </div>
            </div>
            {!isIeltRead &&
              <TestMenu
                activeQuestion={activeQuestion}
                numberQuestionDone={numberQuestionDone}
                handleExamScore={handleExamScore}
                passagesOfUser={passagesOfUser}
                initDataTest={handleResetDataTest}
              />
            }
          </div>
        </div>
      </section >
    </AudioProvider>
  )
}

export const getServerSideProps = wrapper.getServerSideProps(
  (store) => withGlobalData(async ({ locale, query }) => {
    try {
      const [test, translations] = await Promise.all([
        testApi.getByTestSlug(query.slug, { code: locale }),
        serverSideTranslations(locale, ['common']),
      ]);

      store.dispatch(setTest({
        ...test,
        title: test?.title[locale],
        category_id: Number(test.category_id)
      }));

      return {
        props: {
          ...translations,
        },
      };
    } catch (error) {
      console.error(error);
      return { notFound: true };
    }
  })
);
